import { useEffect } from "react";

/**
 * <Seo> — per-page head metadata via direct DOM manipulation.
 *
 * Why not React 19's native metadata hoisting?
 *   When hydrating a prerendered page whose <head> was already populated by a
 *   PREVIOUS React render (captured by Puppeteer), React 19 does not recognize
 *   those existing tags as belonging to the current tree, so it appends new
 *   ones alongside them. Result: two <title>, two canonicals, two robots
 *   directives, plus a hydration-mismatch warning.
 *
 *   Effect-based head manipulation is idempotent: it removes stale tags with
 *   matching selectors and inserts fresh ones, running once on mount and again
 *   on every prop change. This works cleanly during prerender (Puppeteer waits
 *   for the effect to run before capturing) and on hydration (identical output).
 *
 * Route-type-aware props:
 *   - Public/indexable: pass title, description, canonical (+ optional jsonLd).
 *   - Funnel/thank-you noindex: pass { title, noindex: true }.
 *   - Private/admin: pass { title, private: true }.
 *   - NotFound: pass { title, notFound: true }.
 *
 * Never combine `canonical` with `noindex` / `private` / `notFound`.
 */
const MANAGED_ATTR = "data-swipe-seo";

function upsertTitle(text) {
  let el = document.head.querySelector(`title[${MANAGED_ATTR}]`);
  // Also remove any legacy titles left from the base HTML.
  document.head
    .querySelectorAll(`title:not([${MANAGED_ATTR}])`)
    .forEach((n) => n.remove());
  if (!el) {
    el = document.createElement("title");
    el.setAttribute(MANAGED_ATTR, "true");
    document.head.appendChild(el);
  }
  if (el.textContent !== text) el.textContent = text;
}

function upsertMeta(selector, attrs) {
  // Remove any duplicates matching this selector first.
  const existing = Array.from(document.head.querySelectorAll(selector));
  const [keep, ...extras] = existing;
  extras.forEach((n) => n.remove());
  let el = keep;
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  el.setAttribute(MANAGED_ATTR, "true");
  for (const [k, v] of Object.entries(attrs)) {
    if (el.getAttribute(k) !== v) el.setAttribute(k, v);
  }
}

function upsertLink(selector, attrs) {
  const existing = Array.from(document.head.querySelectorAll(selector));
  const [keep, ...extras] = existing;
  extras.forEach((n) => n.remove());
  let el = keep;
  if (!el) {
    el = document.createElement("link");
    document.head.appendChild(el);
  }
  el.setAttribute(MANAGED_ATTR, "true");
  for (const [k, v] of Object.entries(attrs)) {
    if (el.getAttribute(k) !== v) el.setAttribute(k, v);
  }
}

function removeSelector(selector) {
  document.head.querySelectorAll(selector).forEach((n) => n.remove());
}

function upsertJsonLd(payload) {
  const key = `${MANAGED_ATTR}-jsonld`;
  removeSelector(`script[${key}]`);
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.setAttribute(key, "true");
  el.setAttribute(MANAGED_ATTR, "true");
  el.textContent = JSON.stringify(payload);
  document.head.appendChild(el);
}

function applySeo({
  title,
  description,
  canonical,
  ogImage,
  ogType,
  jsonLd,
  noindex,
  isPrivate,
  notFound,
}) {
  const robots = isPrivate
    ? "noindex, nofollow, noarchive"
    : noindex || notFound
    ? "noindex, follow"
    : "index, follow";

  const isPublicIndexable = !isPrivate && !noindex && !notFound;

  // Social preview scope: EVERY public-facing route that could be shared on
  // Facebook / LinkedIn / X / iMessage / etc. gets the branded OG image and
  // Twitter card, even if it is `noindex, follow` (funnel/thank-you pages).
  // Only `private` (admin/auth) and `notFound` are excluded from social
  // preview — those pages should never be shareable with brand chrome.
  const hasSocialPreview = !isPrivate && !notFound;

  // Title (always)
  if (title) upsertTitle(title);

  // Robots (always)
  upsertMeta('meta[name="robots"]', { name: "robots", content: robots });

  // Description — only for indexable pages
  if (isPublicIndexable && description) {
    upsertMeta('meta[name="description"]', {
      name: "description",
      content: description,
    });
  } else {
    removeSelector('meta[name="description"]');
  }

  // Canonical — only for indexable pages
  if (isPublicIndexable && canonical) {
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonical });
  } else {
    removeSelector('link[rel="canonical"]');
  }

  // OG / Twitter — only for indexable pages
  const ogTags = [
    ["og:type", ogType || "website"],
    ["og:site_name", "Swipetronics"],
    ["og:title", title],
    ["og:description", description],
    ["og:url", canonical],
    ["og:image", ogImage],
    ["og:image:secure_url", ogImage],
    ["og:image:type", "image/jpeg"],
    ["og:image:width", "1200"],
    ["og:image:height", "630"],
    ["og:image:alt", "Swipetronics — Smarter Payments. Stronger Business."],
    ["og:locale", "en_US"],
  ];
  const twTags = [
    ["twitter:card", "summary_large_image"],
    ["twitter:title", title],
    ["twitter:description", description],
    ["twitter:image", ogImage],
    ["twitter:image:alt", "Swipetronics — Smarter Payments. Stronger Business."],
  ];

  if (hasSocialPreview) {
    for (const [prop, val] of ogTags) {
      if (val) {
        upsertMeta(`meta[property="${prop}"]`, { property: prop, content: val });
      } else {
        removeSelector(`meta[property="${prop}"]`);
      }
    }
    for (const [name, val] of twTags) {
      if (val) {
        upsertMeta(`meta[name="${name}"]`, { name, content: val });
      } else {
        removeSelector(`meta[name="${name}"]`);
      }
    }
  } else {
    // Non-shareable (private/admin, 404): strip all OG/Twitter tags.
    for (const [prop] of ogTags) removeSelector(`meta[property="${prop}"]`);
    for (const [name] of twTags) removeSelector(`meta[name="${name}"]`);
  }

  // Page-level JSON-LD — only for indexable pages
  if (isPublicIndexable && jsonLd) {
    upsertJsonLd(jsonLd);
  } else {
    removeSelector(`script[${MANAGED_ATTR}-jsonld]`);
  }

  // Fire a signal that Puppeteer's prerender can wait on.
  document.documentElement.setAttribute("data-seo-applied", "true");
}

export default function Seo({
  title,
  description,
  canonical,
  ogImage = "https://swipetronics.com/og-image.jpg?v=4",
  ogType,
  jsonLd,
  noindex = false,
  private: isPrivate = false,
  notFound = false,
}) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    applySeo({
      title,
      description,
      canonical,
      ogImage,
      ogType,
      jsonLd,
      noindex,
      isPrivate,
      notFound,
    });
  }, [title, description, canonical, ogImage, ogType, jsonLd, noindex, isPrivate, notFound]);
  return null;
}
